import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RequestFilters } from 'utils/types/filters';
import { RequestStatus } from 'utils/types/request';
import { REQUEST_TABLE_FILTERS } from 'utils/constants';
import {
  ACTIVE_REQUEST_STATUSES,
  DB_REQUEST_STATUSES,
  DEFAULT_REQUEST_STATUS_FILTER,
} from 'utils/constants/request';

const useFilteredStatuses = (appliedFilters: RequestFilters) => {
  const { advancedRequestsCompletedStatus = false } = useFlags();

  return useMemo(() => {
    const isOpenRequestsFilterApplied =
      appliedFilters[REQUEST_TABLE_FILTERS.OPEN_REQUESTS];
    const rawStatusesFilter = appliedFilters[
      REQUEST_TABLE_FILTERS.STATUS
    ] as readonly RequestStatus[];

    // CGP-183: Completed rides the advanced-requests-completed-status flag end
    // to end. With the flag off it must never reach the API — not even from a
    // filter setting persisted while the flag was on — because a backend
    // build without the CGP-178 enum rejects the whole request with a 400.
    const statusesFilter = advancedRequestsCompletedStatus
      ? rawStatusesFilter
      : rawStatusesFilter?.filter(
          (status) => status !== DB_REQUEST_STATUSES.COMPLETED
        );
    const isStatusFilterApplied = rawStatusesFilter?.length > 0;

    let statuses: readonly RequestStatus[] = statusesFilter;
    let shouldFetchData = true;

    if (isOpenRequestsFilterApplied) {
      if (isStatusFilterApplied) {
        statuses = statusesFilter.filter((status) =>
          (ACTIVE_REQUEST_STATUSES as readonly RequestStatus[]).includes(status)
        );
        if (statuses.length === 0) {
          shouldFetchData = false;
        }
      } else {
        statuses = ACTIVE_REQUEST_STATUSES;
      }
    } else if (!isStatusFilterApplied) {
      // Pass the default explicitly instead of leaning on the getRequests
      // thunk default so the flag decides whether Completed is requested,
      // and so refetch call sites reading the stored filterParameter reuse
      // the same flag-aware list.
      statuses = advancedRequestsCompletedStatus
        ? Object.values(DB_REQUEST_STATUSES)
        : DEFAULT_REQUEST_STATUS_FILTER;
    } else if (statuses.length === 0) {
      // The applied filter contained only Completed and the flag is off:
      // fetch nothing, mirroring the open-requests empty-intersection case.
      shouldFetchData = false;
    }

    return { statuses, shouldFetchData };
  }, [appliedFilters, advancedRequestsCompletedStatus]);
};

export default useFilteredStatuses;
